import { FolderIcon } from '@heroicons/react/24/outline';
import LocationIcon from '../assets/icons/location.svg?react';
import VectorLineIcon from '../assets/icons/vector-line.svg?react';
import VectorPolygonIcon from '../assets/icons/vector-polygon.svg?react';
import CubeIcon from '../assets/icons/cube.svg?react';
import ClusterIcon from '../assets/icons/cluster-icon.svg?react';
import PlaneIcon from '../assets/icons/plane.svg?react';
import RoomIcon from '../assets/icons/Room.svg?react';
import WallIcon from '../assets/icons/wall.svg?react';
import DoorIcon from '../assets/icons/door.svg?react';
import WindowIcon from '../assets/icons/window.svg?react';
import Floor from '../assets/icons/Floor.svg?react';
import BeamIcon from '../assets/icons/Beam.svg?react';
import ColumnIcon from '../assets/icons/Column.svg?react';
import { GroupType } from '../types/graphqlTypes';
import { HeroIcon } from '../types';
import { OpeningKind, StructureKind } from 'shared-types';
import { getOpeningKindProperty, getOpeningSketchKindProperty, getStructureKindProperty, getSketchObjectTypeIdProperty } from '@pointorama/potree-shared';
import { getSketchObjectTypeIcon } from '../potree/sketchObjects/typeIcon';
import { usePropertyValue } from '../modules/Properties/reactive/usePropertyValue';

export const ICONS_BY_TYPE = {
  POINT: LocationIcon,
  DISTANCE: VectorLineIcon,
  AREA: VectorPolygonIcon,
  BOX: CubeIcon,
  POINTCLUSTER: ClusterIcon,
  PLANE: PlaneIcon,
  ROOM: RoomIcon,
  WALL_SKETCH: WallIcon,
  SKETCH_OBJECT: CubeIcon,
  OUTLINE: RoomIcon,
  WINDOW: WindowIcon,
  DOOR: DoorIcon,
  BEAM: BeamIcon,
  COLUMN: ColumnIcon,
} as const;

export type AnnotationTypeName = keyof typeof ICONS_BY_TYPE;

/**
 * Icon lookup with a safe fallback — an unmapped type must not crash the
 * whole annotations panel (React throws on rendering undefined).
 */
function iconForTypeKey(typeKey: string, identifier?: string): AnnotationIcon {
  const icon = ICONS_BY_TYPE[typeKey as AnnotationTypeName];
  if (!icon) {
    console.warn(`[annotationIcons] No icon mapped for annotation type "${typeKey}"`, { identifier });
    return LocationIcon;
  }
  return icon;
}

export function getAnnotationTypeKey(annotation: { type: string; identifier: string }): AnnotationTypeName {
  if (annotation.type === 'OPENING') {
    const kind = getOpeningKindProperty(annotation.identifier).value;
    return kind === OpeningKind.WINDOW ? 'WINDOW' : 'DOOR';
  }
  if (annotation.type === 'OPENING_SKETCH') {
    const kind = getOpeningSketchKindProperty(annotation.identifier).value;
    return kind === OpeningKind.WINDOW ? 'WINDOW' : 'DOOR';
  }
  if (annotation.type === 'STRUCTURE') {
    const kind = getStructureKindProperty(annotation.identifier).value;
    return kind === StructureKind.BEAM ? 'BEAM' : 'COLUMN';
  }
  return (annotation.type || 'POINT') as AnnotationTypeName;
}

/**
 * React hook version of getAnnotationTypeKey that subscribes to opening_kind changes.
 * Re-renders the component when an opening's subtype (DOOR/WINDOW) changes.
 */
export function useAnnotationTypeKey(annotation: { type: string; identifier: string }): AnnotationTypeName {
  const openingKindProperty = getOpeningKindProperty(annotation.identifier);
  const openingKind = usePropertyValue(openingKindProperty);
  const openingSketchKindProperty = getOpeningSketchKindProperty(annotation.identifier);
  const openingSketchKind = usePropertyValue(openingSketchKindProperty);
  const structureKindProperty = getStructureKindProperty(annotation.identifier);
  const structureKind = usePropertyValue(structureKindProperty);

  if (annotation.type === 'OPENING') {
    return openingKind === OpeningKind.WINDOW ? 'WINDOW' : 'DOOR';
  }
  if (annotation.type === 'OPENING_SKETCH') {
    return openingSketchKind === OpeningKind.WINDOW ? 'WINDOW' : 'DOOR';
  }
  if (annotation.type === 'STRUCTURE') {
    return structureKind === StructureKind.BEAM ? 'BEAM' : 'COLUMN';
  }
  return (annotation.type || 'POINT') as AnnotationTypeName;
}

export const GROUP_ICONS_BY_TYPE: Record<GroupType, HeroIcon | React.FC<React.SVGProps<SVGSVGElement>>> = {
  [GroupType.Default]: FolderIcon,
  [GroupType.Floor]: Floor,
  [GroupType.Surface]: PlaneIcon,
  [GroupType.Room]: RoomIcon,
};

type AnnotationIcon = HeroIcon | React.FC<React.SVGProps<SVGSVGElement>>;

/**
 * Icon for an annotation row. Same as `ICONS_BY_TYPE[getAnnotationTypeKey(...)]`
 * (with the unmapped-type fallback) except SKETCH_OBJECTs resolve per object
 * type through the sketch-object registry: the type's declared icon, else one
 * auto-generated from its glyph — every type gets a type-specific icon (see
 * typeIcon.tsx).
 */
export function getAnnotationIcon(annotation: { type: string; identifier: string }): AnnotationIcon {
  if (annotation.type === 'SKETCH_OBJECT') {
    return getSketchObjectTypeIcon(getSketchObjectTypeIdProperty(annotation.identifier).value);
  }
  return iconForTypeKey(getAnnotationTypeKey(annotation), annotation.identifier);
}

/**
 * React hook version of getAnnotationIcon — subscribes to the subtype
 * properties (opening kind, sketch-object type) so rows re-render when the
 * subtype changes.
 */
export function useAnnotationIcon(annotation: { type: string; identifier: string }): AnnotationIcon {
  const typeKey = useAnnotationTypeKey(annotation);
  const sketchObjectTypeId = usePropertyValue(getSketchObjectTypeIdProperty(annotation.identifier));
  if (annotation.type === 'SKETCH_OBJECT') {
    return getSketchObjectTypeIcon(sketchObjectTypeId);
  }
  return iconForTypeKey(typeKey, annotation.identifier);
}
